import { Alert, Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Stack, Tooltip, Typography } from '@mui/material';
import { FunnelBasic, LimitWeights } from 'API/linkedin-funnel-limit-weights';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import PaiAutoComplete, { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import WorkTimeSettings from '../campaign-builder/campaign-building-panel/campaign-settings/work-time-settings';
import {
  MAX_BLOCKS_PER_DAY,
  MAX_BLOCK_HOURS,
  MAX_DAILY_WORK_HOURS,
  MIN_BLOCK_HOURS,
} from '../campaign-builder/campaign-building-panel/campaign-settings/work-blocks';
import { FunnelDays } from '../co-pilot.types';
import { DAILY_LIMIT_RECOMMENDED } from '../../../constants/linkedin-daily-limits';

interface AccountLimits {
  connections: number;
  messages: number;
  emails: number;
  coldCalls: number;
  visits: number;
  inMails: number;
}

interface Props {
  open: boolean;
  loading: boolean;

  // worktime + limits (left side)
  timeZone: OptionType;
  workDays: string[];
  workHours: number[];
  workHoursTrimmedFrom?: number | null;
  connectionsLimit: number | null;
  messagesLimit: number | null;
  emailsLimit: number | null;
  coldCallsLimit: number | null;
  cooldownAfterMessages: number | null;
  cooldownAfterVisits: number | null;
  visitDailyLimit: number | null;
  inmailDailyLimit: number | null;
  handleTimeZoneChange(event: React.ChangeEvent<HTMLInputElement>, newValue: OptionType): void;
  handleWorkDaysChange(value: FunnelDays): () => void;
  handleWorkHoursChange(value: number): () => void;
  handleConnectionsLimitChange(e: React.ChangeEvent<HTMLInputElement>): void;
  handleMessagesLimitChange(e: React.ChangeEvent<HTMLInputElement>): void;
  handleEmailsLimitChange(e: React.ChangeEvent<HTMLInputElement>): void;
  handleColdCallsLimitChange(e: React.ChangeEvent<HTMLInputElement>): void;
  handleCooldownAfterMessagesChange(e: React.ChangeEvent<HTMLInputElement>): void;
  handleCooldownAfterVisitsChange(e: React.ChangeEvent<HTMLInputElement>): void;
  handleVisitDailyLimitChange(e: React.ChangeEvent<HTMLInputElement>): void;
  handleInmailDailyLimitChange(e: React.ChangeEvent<HTMLInputElement>): void;

  // weights (right side)
  funnels: FunnelBasic[];
  accountLimits: AccountLimits;
  editedWeights: Record<string, LimitWeights>;
  onWeightChange(funnelId: string, metric: keyof LimitWeights, value: number): void;

  handleClose(): void;
  handleSave(): void;
  readOnly?: boolean;
}

const metrics: Array<keyof LimitWeights> = ['connections', 'messages', 'emails', 'inMails', 'coldCalls', 'visits'];

const EditAccountLimitsAndWeightsDialog: React.FC<Props> = ({
  open,
  loading,
  timeZone,
  workDays,
  workHours,
  workHoursTrimmedFrom,
  connectionsLimit,
  messagesLimit,
  emailsLimit,
  coldCallsLimit,
  cooldownAfterMessages,
  cooldownAfterVisits,
  visitDailyLimit,
  inmailDailyLimit,
  handleTimeZoneChange,
  handleWorkDaysChange,
  handleWorkHoursChange,
  handleConnectionsLimitChange,
  handleMessagesLimitChange,
  handleEmailsLimitChange,
  handleColdCallsLimitChange,
  handleCooldownAfterMessagesChange,
  handleCooldownAfterVisitsChange,
  handleVisitDailyLimitChange,
  handleInmailDailyLimitChange,
  funnels,
  accountLimits,
  editedWeights,
  onWeightChange,
  handleClose,
  handleSave,
  readOnly = false,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const funnelsPerPage = 5;

  const [showWeights, setShowWeights] = useState(false);

  // Reset to first page when funnels change
  useEffect(() => {
    setCurrentPage(1);
  }, [funnels.length]);

  // Check if a campaign is currently running
  // Check if campaigns have running status (_status "started")
  const isCampaignRunning = (funnel: any): boolean => {
    // Check if campaigns have running status
    return funnel._status === 'started';
  };

  // Sort funnels: Running campaigns first, then by date (newest first)
  const sortedFunnels = [...funnels].sort((a, b) => {
    const aIsRunning = isCampaignRunning(a);
    const bIsRunning = isCampaignRunning(b);

    // First sort by running status (running campaigns first)
    if (aIsRunning && !bIsRunning) return -1;
    if (!aIsRunning && bIsRunning) return 1;

    // Then sort by date (assuming createDate exists, newest first)
    const aDate = (a as any).createDate || (a as any)._createDate || '';
    const bDate = (b as any).createDate || (b as any)._createDate || '';

    if (aDate && bDate) {
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    }

    // Fallback to name if no dates
    return a.name.localeCompare(b.name);
  });

  // totals per metric across edited + original weights
  // Only include running campaigns in the calculation
  const totalWeights = React.useMemo(() => {
    const totals: Record<keyof LimitWeights, number> = {
      connections: 0,
      messages: 0,
      emails: 0,
      coldCalls: 0,
      visits: 0,
      inMails: 0,
    };
    metrics.forEach((m) => {
      totals[m] = sortedFunnels.reduce((acc, f) => {
        // Only include campaigns that are running in the calculation
        if (!isCampaignRunning(f)) return acc;
        return acc + (editedWeights[f._id]?.[m] ?? f.limitWeights?.[m] ?? 1);
      }, 0);
    });
    return totals;
  }, [sortedFunnels, editedWeights]);

  // Pagination logic
  const indexOfLastFunnel = currentPage * funnelsPerPage;
  const indexOfFirstFunnel = indexOfLastFunnel - funnelsPerPage;
  const currentFunnels = sortedFunnels.slice(indexOfFirstFunnel, indexOfLastFunnel);
  const totalPages = Math.ceil(sortedFunnels.length / funnelsPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const renderNumericField = (
    value: number | null,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    recommended?: number,
    disabled?: boolean
  ) => (
    <Stack gap={0.5}>
      <PaiTextField type="number" value={`${value ?? ''}`} onChange={onChange} style={{ maxWidth: '100%' }} disabled={disabled} />
      {!recommended ? null : (
        <Typography variant="caption" color="text.secondary">
          {t('recommendedDailyMax', { max: recommended })}
        </Typography>
      )}
    </Stack>
  );

  const timeZones: string[] = (Intl as any).supportedValuesOf('timeZone');
  const timeZonesOptions = timeZones.map((zone) => ({ label: zone, value: zone }));

  const handleToggleWeights = () => {
    setShowWeights((prev) => !prev);
  };

  return (
    <Dialog open={open} fullWidth maxWidth={'lg'} classes={{ paper: classes.paper }}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('editLimitsAndAccountWorkingTime')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={3}>
          <div>
            <Typography className={classes.sectionTitle} mb={'8px'}>
              {t('selectTimezone')}
            </Typography>
            <Typography className={classes.greyText} mb={'16px'}>
              {t('selectTimezoneDescription')}
            </Typography>
            <PaiAutoComplete
              options={timeZonesOptions}
              selected={timeZone || { label: '', value: '' }}
              disableClearable={true}
              onChange={handleTimeZoneChange}
              style={{ maxWidth: '450px' }}
              disabled={readOnly}
            />
          </div>
          <Divider />

          <Stack gap={3}>
            <Typography className={classes.sectionTitle}>{t('defineCampaignWorkTime')}</Typography>
            <Stack gap={1}>
              <Typography variant={'caption'} color={'textSecondary'}>
                {t('workHoursRuleHint', {
                  maxBlocks: MAX_BLOCKS_PER_DAY,
                  minLength: MIN_BLOCK_HOURS,
                  maxLength: MAX_BLOCK_HOURS,
                  maxHours: MAX_DAILY_WORK_HOURS,
                })}
              </Typography>
              {!workHoursTrimmedFrom ? null : (
                <Alert severity="info" variant="outlined">
                  {t('workHoursTrimmedNotice', { previous: workHoursTrimmedFrom, max: MAX_DAILY_WORK_HOURS })}
                </Alert>
              )}
            </Stack>
            <WorkTimeSettings
              workDays={workDays as any}
              workHours={workHours as any}
              variant="border"
              handleWorkDaysChange={readOnly ? () => () => {} : handleWorkDaysChange}
              handleWorkHoursChange={readOnly ? () => () => {} : handleWorkHoursChange}
            />

            <Divider />

            <div>
              <Typography className={classes.sectionTitle} mb={'8px'}>
                {t('dailyActivityLimits')}
              </Typography>
              <Typography className={classes.greyText} mb={'16px'}>
                {t('defineDailyMessages')}
              </Typography>
              <Grid container spacing={2}>
                <Grid size={4}>
                  <Stack gap={0.5} sx={{ minWidth: 200 }}>
                    <Typography className={classes.label}>{t('connectionRequests')}:</Typography>
                    {renderNumericField(
                      connectionsLimit,
                      handleConnectionsLimitChange,
                      DAILY_LIMIT_RECOMMENDED.connections,
                      readOnly
                    )}
                  </Stack>
                </Grid>

                <Grid size={4}>
                  <Stack gap={0.5} sx={{ minWidth: 200 }}>
                    <Typography className={classes.label}>{t('messages')}:</Typography>
                    {renderNumericField(
                      messagesLimit,
                      handleMessagesLimitChange,
                      DAILY_LIMIT_RECOMMENDED.messages,
                      readOnly
                    )}
                  </Stack>
                </Grid>

                <Grid size={4}>
                  <Stack gap={0.5} sx={{ minWidth: 200 }}>
                    <Typography className={classes.label}>{t('emails')}:</Typography>
                    {renderNumericField(emailsLimit, handleEmailsLimitChange, DAILY_LIMIT_RECOMMENDED.emails, readOnly)}
                  </Stack>
                </Grid>

                <Grid size={4}>
                  <Stack gap={0.5} sx={{ minWidth: 200 }}>
                    <Typography className={classes.label}>{t('inMails')}:</Typography>
                    {renderNumericField(inmailDailyLimit, handleInmailDailyLimitChange, DAILY_LIMIT_RECOMMENDED.inmails, readOnly)}
                  </Stack>
                </Grid>

                <Grid size={4}>
                  <Stack gap={0.5} sx={{ minWidth: 200 }}>
                    <Typography className={classes.label}>{t('coldCalls')}:</Typography>
                    {renderNumericField(coldCallsLimit, handleColdCallsLimitChange, undefined, readOnly)}
                  </Stack>
                </Grid>

                <Grid size={4} />

                <Grid size={4}>
                  <Stack gap={0.5} sx={{ minWidth: 200 }}>
                    <Typography className={classes.label}>{t('profileVisits')}:</Typography>
                    {renderNumericField(visitDailyLimit, handleVisitDailyLimitChange, DAILY_LIMIT_RECOMMENDED.visits, readOnly)}
                  </Stack>
                </Grid>

                <Grid size={4}>
                  <Stack gap={0.5} sx={{ minWidth: 200 }}>
                    <Typography className={classes.label}>{t('cooldownAfterMessages')}:</Typography>
                    {renderNumericField(cooldownAfterMessages, handleCooldownAfterMessagesChange, undefined, readOnly)}
                  </Stack>
                </Grid>

                <Grid size={4}>
                  <Stack gap={0.5} sx={{ minWidth: 200 }}>
                    <Typography className={classes.label}>{t('cooldownAfterVisits')}:</Typography>
                    {renderNumericField(cooldownAfterVisits, handleCooldownAfterVisitsChange, undefined, readOnly)}
                  </Stack>
                </Grid>
              </Grid>
            </div>
          </Stack>

          <Divider />

          <Stack gap={3}>
            <div>
              <Typography className={classes.sectionTitle} mb={'8px'}>
                {t('quotaSplit')}
              </Typography>
              <Typography className={classes.greyText} mb={'8px'}>
                {t('defineQuotaSplitDescription')}
              </Typography>
              <PaiLink
                text={showWeights ? t('hideWeights') : t('adjustWeights')}
                size={'lg'}
                variant={'default'}
                underline={'always'}
                action={handleToggleWeights}
              />
            </div>

            {showWeights ? (
              <Stack gap={2} p={3} bgcolor="white" borderRadius="12px" border="1px solid" borderColor="greys.light">
                {/* Header Row */}
                <Stack direction="row" gap={2} alignItems="center" p={2} bgcolor="grey.50" borderRadius="8px">
                  <Typography className={classes.header} sx={{ flex: 1 }}>
                    {t('campaign')}
                  </Typography>
                  {metrics.map((m) => (
                    <Typography key={m} className={classes.header} sx={{ flex: 1, textAlign: 'center' }}>
                      {t(m)}
                    </Typography>
                  ))}
                </Stack>

                {/* Account Limits Row */}
                <Stack direction="row" gap={2} alignItems="center" p={2} bgcolor="grey.25" borderRadius="8px">
                  <Typography className={classes.header} sx={{ flex: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                    {t('currentSettings')}
                  </Typography>
                  {metrics.map((m) => (
                    <Typography key={m} className={classes.header} sx={{ flex: 1, textAlign: 'center', fontWeight: 'bold', color: 'text.secondary' }}>
                      {accountLimits[m]}/{t('day')}
                    </Typography>
                  ))}
                </Stack>

                <Divider />

                {/* Campaign Rows */}
                {currentFunnels.map((f, index) => {
                  const isRunning = isCampaignRunning(f);
                  return (
                    <React.Fragment key={f._id}>
                      <Box
                        sx={(theme) => ({
                          fontWeight: 'normal',
                          borderRadius: '8px',
                          border: isRunning ? 2 : 0,
                          borderColor: isRunning ? theme.palette.success.main : 'transparent',
                          backgroundColor: isRunning ? theme.palette.success.light : 'transparent',
                          padding: '0 16px',
                          position: 'relative',
                        })}
                      >
                        {/* Running Campaign Badge */}
                        {isRunning && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 8,
                              backgroundColor: 'success.main',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: 'bold',
                            }}
                          >
                            RUNNING
                          </Box>
                        )}

                        {/* Calculations Row - Perfectly Aligned */}
                        <Stack direction="row" gap={2} alignItems="center" mb={0.5}>
                          <Box sx={{ flex: 1 }}>
                            {/* Campaign Name and Badge Row */}
                            <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-start'} gap={1}>
                              <Tooltip title={f.name} arrow placement="top">
                                <Typography
                                  className={classes.header}
                                  sx={{
                                    fontWeight: isRunning ? 'bold' : 'normal',
                                    color: isRunning ? 'success.dark' : 'inherit',
                                  }}
                                >
                                  {_.truncate(f.name, { length: 25 })}
                                </Typography>
                              </Tooltip>
                              {isRunning && <Chip label={t('running')} size="small" color="success" />}
                            </Stack>
                          </Box>
                          {metrics.map((m) => {
                            const weightValue = editedWeights[f._id]?.[m] ?? f.limitWeights?.[m] ?? 1;
                            const totalWeight = totalWeights[m];
                            // Only calculate percentage and quota for running campaigns
                            const percentage = isRunning && totalWeight ? ((weightValue / totalWeight) * 100).toFixed(0) : '0';
                            const dailyQuota =
                              isRunning && accountLimits[m] && totalWeight ? Math.floor((accountLimits[m] * weightValue) / totalWeight) : 0;

                            return (
                              <Box key={m} sx={{ flex: 1, textAlign: 'center' }}>
                                <Typography
                                  variant="caption"
                                  color={isRunning ? 'success.dark' : 'text.secondary'}
                                  sx={{
                                    minHeight: '16px',
                                    display: 'block',
                                    fontWeight: isRunning ? 'bold' : 'normal',
                                  }}
                                >
                                  {isRunning ? `${percentage}% → ${dailyQuota}` : 'Not Running'}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Stack>

                        {/* Input Fields Row */}
                        <Stack direction="row" gap={2} alignItems="center">
                          <Box sx={{ flex: 1 }}>{/* Empty space to align with campaign name */}</Box>
                          {metrics.map((m) => {
                            const weightValue = editedWeights[f._id]?.[m] ?? f.limitWeights?.[m] ?? 1;

                            return (
                              <Box key={m} sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                                <PaiTextField
                                  type="number"
                                  value={weightValue}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onWeightChange(f._id, m, Number(e.target.value))}
                                  min={0}
                                />
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                      {(index < currentFunnels.length - 1 || totalPages > 1) && <Divider sx={{ my: 0.5 }} />}
                    </React.Fragment>
                  );
                })}

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <PaiPagination totalPages={totalPages} page={currentPage} handleChangePage={handlePageChange} />
                  </Box>
                )}
              </Stack>
            ) : null}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton text={t('save')} size={'small'} border={'rounded'} action={handleSave} loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
    padding: '20px',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  sectionTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  label: {
    fontSize: theme.typography.pxToRem(15),
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  header: {
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(15),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default EditAccountLimitsAndWeightsDialog;
